import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import BrilloLogo from "@/components/BrilloLogo";
import api, {
  TIPO_CREDITO_OPTIONS,
  INGRESOS_OPTIONS,
  labelOf,
} from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users,
  DollarSign,
  MessageSquare,
  TrendingUp,
  Download,
  Search,
  LogOut,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const fmtCurrency = (v) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return new Intl.DateTimeFormat("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(location.state?.user || null);
  const [authChecking, setAuthChecking] = useState(!location.state?.user);
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tipoFilter, setTipoFilter] = useState("all");
  const [ingresosFilter, setIngresosFilter] = useState("all");

  useEffect(() => {
    if (user) return;
    const check = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        navigate("/login", { replace: true });
      } finally {
        setAuthChecking(false);
      }
    };
    check();
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true);
      try {
        const [l, c, s] = await Promise.all([
          api.get("/admin/leads"),
          api.get("/admin/contacts"),
          api.get("/admin/stats"),
        ]);
        setLeads(l.data.items || []);
        setContacts(c.data.items || []);
        setStats(s.data);
      } catch (e) {
        toast.error("No se pudieron cargar los datos.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const filteredLeads = useMemo(() => {
    return leads.filter((l) => {
      const matchesQ =
        !q ||
        (l.nombre || "").toLowerCase().includes(q.toLowerCase()) ||
        (l.email || "").toLowerCase().includes(q.toLowerCase()) ||
        (l.whatsapp || "").includes(q);
      const matchesTipo = tipoFilter === "all" || l.tipo_credito === tipoFilter;
      const matchesIng = ingresosFilter === "all" || l.ingresos === ingresosFilter;
      return matchesQ && matchesTipo && matchesIng;
    });
  }, [leads, q, tipoFilter, ingresosFilter]);

  const exportCsv = () => {
    const rows = [
      ["Fecha", "Nombre", "WhatsApp", "Email", "Tipo de crédito", "Monto (MXN)", "Ingresos"],
      ...filteredLeads.map((l) => [
        fmtDate(l.created_at),
        l.nombre,
        l.whatsapp,
        l.email,
        labelOf(TIPO_CREDITO_OPTIONS, l.tipo_credito),
        l.monto,
        labelOf(INGRESOS_OPTIONS, l.ingresos),
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `brillo-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout failed:", err);
    }
    navigate("/login", { replace: true });
  };

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-6 w-6 animate-spin text-[#04b0ba]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="admin-dashboard">
      {/* Topbar */}
      <header className="sticky top-0 z-30 border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BrilloLogo className="h-9 w-auto" />
            <span className="hidden md:inline text-xs uppercase tracking-[0.2em] text-[#666666]">
              Panel administrador
            </span>
          </div>
          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden md:flex flex-col items-end">
                <span className="text-sm text-[#333333] font-medium" data-testid="admin-user-name">
                  {user.name || user.email}
                </span>
                <span className="text-xs text-[#666666]">{user.email}</span>
              </div>
            )}
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-[#e5e7eb] px-4 py-2 text-sm text-[#333333] hover:border-[#f05c3e] hover:text-[#f05c3e] transition-colors"
            >
              <LogOut className="h-4 w-4" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <StatCard
            icon={Users}
            label="Prospectos totales"
            value={stats?.total_leads ?? 0}
            accent="#f05c3e"
            testId="stat-total-leads"
          />
          <StatCard
            icon={TrendingUp}
            label="Últimos 7 días"
            value={stats?.last_7_days ?? 0}
            accent="#04b0ba"
            testId="stat-last-7"
          />
          <StatCard
            icon={DollarSign}
            label="Monto solicitado total"
            value={fmtCurrency(stats?.total_monto ?? 0)}
            accent="#333333"
            testId="stat-total-monto"
          />
          <StatCard
            icon={MessageSquare}
            label="Mensajes de contacto"
            value={stats?.total_contacts ?? 0}
            accent="#04b0ba"
            testId="stat-total-contacts"
          />
        </div>

        <Tabs defaultValue="leads" className="mt-10">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="leads" data-testid="tab-leads">Prospectos</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Mensajes</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-6">
            <div className="rounded-2xl border border-[#e5e7eb] bg-white">
              <div className="p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between border-b border-[#e5e7eb]">
                <div className="flex flex-col md:flex-row gap-3 md:items-center flex-1">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666666]" />
                    <Input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Buscar por nombre, email o WhatsApp"
                      className="pl-9"
                      data-testid="admin-search-input"
                    />
                  </div>
                  <Select value={tipoFilter} onValueChange={setTipoFilter}>
                    <SelectTrigger className="max-w-[220px]" data-testid="filter-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los tipos</SelectItem>
                      {TIPO_CREDITO_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={ingresosFilter} onValueChange={setIngresosFilter}>
                    <SelectTrigger className="max-w-[220px]" data-testid="filter-ingresos">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los ingresos</SelectItem>
                      {INGRESOS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button
                  onClick={exportCsv}
                  data-testid="admin-export-csv"
                  className="inline-flex items-center gap-2 rounded-full bg-[#333333] text-white px-4 py-2 text-sm hover:bg-black transition-colors"
                >
                  <Download className="h-4 w-4" /> Exportar CSV
                </button>
              </div>

              <div className="overflow-x-auto">
                {loading ? (
                  <div className="p-16 flex justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-[#04b0ba]" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead>WhatsApp</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                        <TableHead>Ingresos</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredLeads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-10 text-[#666666]">
                            Aún no hay prospectos que coincidan.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredLeads.map((l) => (
                          <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                            <TableCell className="text-xs text-[#666666]">{fmtDate(l.created_at)}</TableCell>
                            <TableCell className="font-medium">{l.nombre}</TableCell>
                            <TableCell>
                              <a
                                href={`https://wa.me/${l.whatsapp.replace(/\D/g, "")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bf-link"
                              >
                                {l.whatsapp}
                              </a>
                            </TableCell>
                            <TableCell>
                              <a href={`mailto:${l.email}`} className="bf-link">
                                {l.email}
                              </a>
                            </TableCell>
                            <TableCell>{labelOf(TIPO_CREDITO_OPTIONS, l.tipo_credito)}</TableCell>
                            <TableCell className="text-right font-mono">{fmtCurrency(l.monto)}</TableCell>
                            <TableCell>{labelOf(INGRESOS_OPTIONS, l.ingresos)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            <div className="rounded-2xl border border-[#e5e7eb] bg-white overflow-x-auto">
              {loading ? (
                <div className="p-16 flex justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-[#04b0ba]" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Mensaje</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-10 text-[#666666]">
                          Sin mensajes por ahora.
                        </TableCell>
                      </TableRow>
                    ) : (
                      contacts.map((c) => (
                        <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                          <TableCell className="text-xs text-[#666666]">{fmtDate(c.created_at)}</TableCell>
                          <TableCell className="font-medium">{c.nombre}</TableCell>
                          <TableCell>
                            <a href={`mailto:${c.email}`} className="bf-link">{c.email}</a>
                          </TableCell>
                          <TableCell className="max-w-[520px]">
                            <p className="text-sm text-[#333333] whitespace-pre-wrap">{c.mensaje}</p>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="rounded-2xl border border-[#e5e7eb] bg-white p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}15` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
      </div>
      <div className="mt-4 font-display text-2xl text-[#333333]">{value}</div>
      <div className="text-xs text-[#666666] mt-1 uppercase tracking-[0.15em]">{label}</div>
    </div>
  );
}
